'use client';

import React, { useState, useCallback, useEffect } from 'react';
import {
  Button,
  IconSearch,
  IconArrowBackward,
  IconClose
} from '@sympla/sympla-components';

import EmptySearchResults from 'components/EmptySearchResults/EmptySearchResults';
import HighlightCollections from 'components/HighlightCollections/HighlightCollections';

import { userTokenAlgolia } from 'utils/algolia';
import { isMobile as getIsMobile } from 'utils/misc';

import AlgoliaPopularSearch from 'components/AlgoliaSearch/components/AlgoliaPopularSearch';
import RecentSearches from 'components/RecentSearches/RecentSearches';
import AlgoliaPopularEvents from 'components/AlgoliaSearch/components/AlgoliaPopularEvents';
import TrendingEventsFetcher from 'components/TrendingEvents/TrendingEvents';
import { EMPTY_STATES } from 'constants/emptyStates';
import { ALGOLIA_NUMBER_CARACTER } from 'constants/algolia';
import { analytics, analyticsEvents } from 'lib/analytics';
import locationStore from 'stores/LocationStore';

import * as styles from './SearchSuggestions.css';

type SearchSuggestionsProps = {
  onClickInside?: (e: React.MouseEvent) => void;
  handleSearch: (event: React.FormEvent<HTMLFormElement>) => void;
  inputValue: string;
  handleTextChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  inputRef?: React.RefObject<HTMLInputElement>;
  formRef?: React.RefObject<HTMLFormElement>;
  isNavbarCollapsed: boolean;
  onClose: () => void;
  handleClickOutside: () => void;
  setInputValue: (value: string) => void;
  hasMossColor: boolean;
  useNewProposalSearch?: boolean;
  labelTagsAnalytics?: string[];
};

function SearchSuggestions({
  onClickInside,
  onClose,
  handleSearch,
  inputValue,
  handleTextChange,
  inputRef,
  formRef,
  isNavbarCollapsed,
  handleClickOutside,
  setInputValue,
  hasMossColor,
  useNewProposalSearch,
  labelTagsAnalytics = []
}: SearchSuggestionsProps) {
  const [debouncedInputValue, setDebouncedInputValue] = useState(inputValue);

  const [eventsState, setEventsState] = useState({
    hasHits: true,
    isLoading: true
  });

  const isMobile = getIsMobile();
  const DEBOUNCE_TIME = 300;

  const userCity = locationStore.selectedLocation?.city ?? undefined;

  const isValidSearchTerm =
    (debouncedInputValue?.trim().length || 0) >= ALGOLIA_NUMBER_CARACTER;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedInputValue(inputValue);
    }, DEBOUNCE_TIME);

    return () => {
      clearTimeout(timer);
    };
  }, [inputValue]);

  useEffect(() => {
    if (isValidSearchTerm) {
      analytics.sendEvent(analyticsEvents.search_submitted, {
        search_term: debouncedInputValue,
        search_location_set: userCity,
        search_filter_applied: undefined
      });
    }
  }, [debouncedInputValue, isValidSearchTerm, userCity]);

  const queryToSearch = isValidSearchTerm ? debouncedInputValue : '';

  const handleEventsStateChange = useCallback(
    (hasHits: boolean, isLoading: boolean) => {
      setEventsState({ hasHits, isLoading });
    },
    []
  );

  const hasNoContentAtAll =
    isValidSearchTerm && !eventsState.isLoading && !eventsState.hasHits;

  const shouldShowTrendingFallbackDesktop =
    !isValidSearchTerm || hasNoContentAtAll;

  const defaultEventHitsLimit = 6;
  const mobileHitsLimit = 5;
  const algoliaEventsHitsLimit = isMobile
    ? mobileHitsLimit
    : defaultEventHitsLimit;

  const FixedMenuBlock = (
    <div
      className={styles.searchResults}
      data-testid="search-fixed-suggestions"
    >
      <HighlightCollections />
      <AlgoliaPopularSearch
        userTokenAlgolia={userTokenAlgolia}
        hasMossColor={hasMossColor}
        onClose={onClose}
        setInputValue={setInputValue}
        query={queryToSearch}
        labelTagsAnalytics={labelTagsAnalytics}
      />
      <RecentSearches
        hasMossColor={hasMossColor}
        onClose={onClose}
        setInputValue={setInputValue}
      />
    </div>
  );

  const AlgoliaResultsBlock = isValidSearchTerm ? (
    <div className="algolia-wrapper" data-testid="search-algolia-results">
      <AlgoliaPopularEvents
        query={queryToSearch}
        isMobile={isMobile}
        userTokenAlgolia={userTokenAlgolia}
        hitsLimit={algoliaEventsHitsLimit}
        isNavbarCollapsed={isNavbarCollapsed}
        hasMossColor={hasMossColor}
        onClose={onClose}
        onStateChange={handleEventsStateChange}
        labelTagsAnalytics={labelTagsAnalytics}
      />
    </div>
  ) : null;

  const TrendingBlock = (
    <div className={styles.trendingFallback} data-testid="trending-fallback">
      <TrendingEventsFetcher
        hitsLimit={defaultEventHitsLimit}
        isNavbarCollapsed={isNavbarCollapsed}
        hasMossColor={hasMossColor}
        onClose={onClose}
      />
    </div>
  );

  let contentBlocks: React.ReactNode;

  if (isMobile) {
    contentBlocks = (
      <>
        <div className={`${styles.trendingEvents} algolia-event-list-wrapper`}>
          {AlgoliaResultsBlock}
        </div>
        {FixedMenuBlock}
        {TrendingBlock}
      </>
    );
  } else {
    contentBlocks = (
      <>
        {FixedMenuBlock}
        <div className={`${styles.trendingEvents} algolia-event-list-wrapper`}>
          {AlgoliaResultsBlock}
          {shouldShowTrendingFallbackDesktop && TrendingBlock}
        </div>
      </>
    );
  }

  const inputSearchStyle = `${styles.searchForm} ${
    hasMossColor ? styles.searchFormMoss : ''
  }`;

  return (
    <>
      <div
        className={styles.dropdownWrapper}
        data-testid="search-algolia-dropdown-items"
        onClick={onClickInside}
      >
        <div className={styles.searchAndResultsContainer}>
          {(!isNavbarCollapsed || isMobile) && (
            <div className={styles.searchContainer}>
              <div className={styles.searchBox}>
                <div
                  className={`${styles.backButton} ${hasMossColor && styles.backButtonMoss}`}
                >
                  <Button
                    variant="text"
                    icon={<IconArrowBackward width={16} height={16} />}
                    upperCase={false}
                    onClick={onClose}
                  >
                    Voltar
                  </Button>
                </div>
                <form
                  ref={formRef}
                  onSubmit={handleSearch}
                  className={inputSearchStyle}
                  role="presentation"
                  data-testid="search-algolia-form"
                >
                  <IconSearch width={24} height={24} />
                  <input
                    ref={inputRef}
                    className={styles.searchInput}
                    placeholder="Buscar experiências"
                    type="text"
                    autoFocus={!useNewProposalSearch}
                    onChange={handleTextChange}
                    value={inputValue}
                    data-testid="search-algolia-input"
                  />
                  {inputValue && (
                    <div className={styles.closeButtonContainer}>
                      <IconClose
                        width={20}
                        height={20}
                        aria-label="Limpar busca"
                        className={styles.cleanInput}
                        onClick={() => setInputValue('')}
                      />
                    </div>
                  )}
                </form>
              </div>
              <div className={styles.closeButtonContainer}>
                <Button
                  onClick={onClose}
                  upperCase={false}
                  size="small"
                  variant="text"
                  className={styles.closeButton}
                  aria-label="Fechar"
                >
                  <span>Fechar</span>
                  <IconClose width={16} height={16} onClick={onClose} />
                </Button>
              </div>
            </div>
          )}
          <div className={styles.searchContent}>
            {hasNoContentAtAll && (
              <div
                className={styles.emptyResultsContainer}
                data-testid="search-trending-no-results"
              >
                <EmptySearchResults
                  title={EMPTY_STATES.NO_RESULTS.title}
                  subtitle={EMPTY_STATES.NO_RESULTS.subtitle}
                  hasMargin={true}
                />
              </div>
            )}
            <div className={`${styles.resultsAndTrendingContainer}`}>
              {contentBlocks}
            </div>
          </div>
        </div>
      </div>
      <div className={styles.bottomOverlay} onClick={handleClickOutside} />
    </>
  );
}

export default SearchSuggestions;
